/**
 * Dashboard Filter URL Synchronization
 *
 * Utilities for encoding/decoding dashboard filters to/from URL query parameters
 * with validation to prevent malicious or malformed URL parameters
 */

import { analyticsQuerySchema } from '@skewb-works/contracts';
import type { DashboardFilters } from '../types/analytics';

/**
 * Zod schema for validating URL parameter values
 * Reuses the contract schema to ensure consistency with API validation
 */
const UrlFilterSchema = analyticsQuerySchema;

/**
 * Encode filters to URLSearchParams
 */
export function filtersToSearchParams(filters: DashboardFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.dateRange !== 'last6m') {
    params.set('dateRange', filters.dateRange);
  }
  if (filters.jobType !== 'all') {
    params.set('jobType', filters.jobType);
  }
  if (filters.assignee !== 'all') {
    params.set('assignee', filters.assignee);
  }
  if (filters.priority !== 'all') {
    params.set('priority', filters.priority);
  }

  return params;
}

/**
 * Decode filters from URLSearchParams with validation
 * Returns null if no filters in URL or if validation fails
 *
 * Security measures:
 * - Validates dateRange against whitelist
 * - Ensures proper format for IDs
 * - Enforces length limits
 * - Protects against XSS and injection attempts via URL manipulation
 */
export function searchParamsToFilters(searchParams: URLSearchParams): DashboardFilters | null {
  const dateRange = searchParams.get('dateRange');
  const jobType = searchParams.get('jobType');
  const assignee = searchParams.get('assignee');
  const priority = searchParams.get('priority');

  // If no filters in URL, return null
  if (!dateRange && !jobType && !assignee && !priority) {
    return null;
  }

  // Build object to validate
  const rawFilters = {
    dateRange: dateRange || undefined,
    jobType: jobType || undefined,
    assignee: assignee || undefined,
    priority: priority || undefined,
  };

  // Validate with Zod schema
  const result = UrlFilterSchema.safeParse(rawFilters);

  if (!result.success) {
    // Log validation errors (malicious URLs won't crash the app)
    console.warn('Invalid filter values in URL:', result.error.format());
    return null;
  }

  // Return validated filters with defaults for missing values
  return {
    dateRange: (result.data.dateRange as DashboardFilters['dateRange']) || 'last6m',
    jobType: result.data.jobType || 'all',
    assignee: result.data.assignee || 'all',
    priority: result.data.priority || 'all',
  };
}

/**
 * Check if filters are at default values (no need to add to URL)
 */
export function areFiltersDefault(filters: DashboardFilters): boolean {
  return (
    filters.dateRange === 'last6m' &&
    filters.jobType === 'all' &&
    filters.assignee === 'all' &&
    filters.priority === 'all'
  );
}
